/**
 * Office reports (Phase 4). Pick a report, fill in its fields, Run. Manager and Admin also get
 * Export CSV (the server refuses anyone else and audit-logs every export with its row count).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { STATUS_LABELS, WO_STATUSES, type Me } from '@wo/shared';
import { api, errorText, qs } from '../lib/api';
import { dateTime } from '../lib/format';
import { Button, Card, ErrorText, Field, Input, Loading, PageHeader, Select } from '../components/ui';

type FieldType = 'date' | 'mechanic' | 'status' | 'text' | 'machine';
interface ReportInfo {
  key: string;
  title: string;
  description: string;
  fields: { key: string; label: string; type: FieldType }[];
  columns: { key: string; label: string; type: string }[];
}
interface ReportData {
  columns: ReportInfo['columns'];
  rows: Record<string, string | number | boolean | null>[];
  summary?: { label: string; value: string }[];
}

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function defaults(r: ReportInfo): Record<string, string> {
  const now = new Date();
  const out: Record<string, string> = {};
  for (const f of r.fields) {
    if (f.type === 'date') out[f.key] = f.key === 'from' ? ymd(new Date(now.getFullYear(), now.getMonth(), 1)) : ymd(now);
    else out[f.key] = '';
  }
  return out;
}

function show(v: string | number | boolean | null, type: string): string {
  if (v === null || v === undefined || v === '') return '—';
  if (type === 'datetime') return dateTime(String(v));
  if (type === 'bool') return v ? 'Yes' : '—';
  if (type === 'money') return `$${v}`;
  return String(v);
}

export function ReportsPage({ me }: { me: Me }) {
  const canExport = me.role === 'ADMIN' || me.role === 'MANAGER';
  const reports = useQuery({ queryKey: ['reports'], queryFn: () => api.get<ReportInfo[]>('/api/reports') });
  const board = useQuery({ queryKey: ['board'], queryFn: () => api.get<{ mechanics: { user: { id: number; name: string } }[] }>('/api/board') });
  const [key, setKey] = useState('');
  const [params, setParams] = useState<Record<string, string>>({});
  const [data, setData] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const report = useMemo(() => reports.data?.find((r) => r.key === key) ?? reports.data?.[0], [reports.data, key]);
  if (reports.isLoading || !report) return <Loading />;

  const pick = (k: string) => {
    const r = reports.data!.find((x) => x.key === k)!;
    setKey(k);
    setParams(defaults(r));
    setData(null);
    setError(null);
  };
  const values = Object.keys(params).length ? params : defaults(report);
  const query = qs(values);

  const run = async () => {
    setError(null);
    setRunning(true);
    try {
      setData(await api.get<ReportData>(`/api/reports/${report.key}${query}`));
    } catch (e) {
      setError(errorText(e));
      setData(null);
    } finally {
      setRunning(false);
    }
  };

  const set = (k: string) => (e: { target: { value: string } }) => setParams({ ...values, [k]: e.target.value });

  return (
    <>
      <PageHeader title="Reports" />
      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <Card title="Report">
          <ul className="space-y-1">
            {reports.data!.map((r) => (
              <li key={r.key}>
                <button
                  type="button"
                  onClick={() => pick(r.key)}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm ${r.key === report.key ? 'bg-blue-50 font-semibold text-blue-900' : 'hover:bg-slate-50'}`}
                >
                  {r.title}
                </button>
              </li>
            ))}
          </ul>
        </Card>
        <div className="space-y-4">
          <Card title={report.title}>
            <p className="mb-3 text-sm text-slate-600">{report.description}</p>
            <div className="flex flex-wrap items-end gap-3">
              {report.fields.map((f) => (
                <div key={f.key} className="w-48">
                  <Field label={f.label}>
                    {f.type === 'date' ? (
                      <Input type="date" value={values[f.key] ?? ''} onChange={set(f.key)} />
                    ) : f.type === 'mechanic' ? (
                      <Select value={values[f.key] ?? ''} onChange={set(f.key)}>
                        <option value="">All mechanics</option>
                        {board.data?.mechanics.map(({ user: m }) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </Select>
                    ) : f.type === 'status' ? (
                      <Select value={values[f.key] ?? ''} onChange={set(f.key)}>
                        <option value="">All open</option>
                        {WO_STATUSES.filter((s) => s !== 'CLOSED' && s !== 'VOIDED').map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </Select>
                    ) : (
                      <Input value={values[f.key] ?? ''} onChange={set(f.key)} />
                    )}
                  </Field>
                </div>
              ))}
              <Button onClick={run} disabled={running}>{running ? 'Running…' : 'Run'}</Button>
              {canExport && (
                <a className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50" href={`/api/reports/${report.key}/export.csv${query}`}>
                  Export CSV
                </a>
              )}
            </div>
            {canExport && <p className="mt-2 text-xs text-slate-500">Every CSV export is recorded in the audit log.</p>}
          </Card>
          <ErrorText error={error} />
          {data && (
            <Card title={`${data.rows.length} row${data.rows.length === 1 ? '' : 's'}`}>
              {data.summary && data.summary.length > 0 && (
                <dl className="mb-4 flex flex-wrap gap-2">
                  {data.summary.map((s) => (
                    <div key={s.label} className="rounded-md bg-slate-100 px-3 py-1 text-sm">
                      <dt className="inline text-slate-600">{s.label}: </dt>
                      <dd className="inline font-semibold">{s.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs uppercase text-slate-500">
                    <tr>{data.columns.map((c) => <th key={c.key} className={`whitespace-nowrap px-3 py-2 ${['money', 'hours', 'int', 'decimal'].includes(c.type) ? 'text-right' : ''}`}>{c.label}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.rows.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        {data.columns.map((c) => (
                          <td key={c.key} className={`px-3 py-1.5 ${['money', 'hours', 'int', 'decimal'].includes(c.type) ? 'text-right tabular-nums' : ''}`}>{show(r[c.key] ?? null, c.type)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.rows.length === 0 && <p className="px-3 py-6 text-center text-sm text-slate-500">No rows.</p>}
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
